import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Download, Calendar, User, Star, MessageSquare, RefreshCw,
  FileText, FileSpreadsheet, FileDown, X, AlertTriangle, Brain,
  TrendingUp, Smile, Meh, Frown, ChevronDown, ChevronUp, Image as ImageIcon
} from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './GoogleMaps.css';

// Map backend sort options to /api/reviews sortBy values
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'highest', label: 'Highest rating' },
  { value: 'lowest', label: 'Lowest rating' }
];

// Quick sentiment/urgency chips — simple client-side filters
const QUICK_FILTERS = [
  { value: 'all',      label: 'All' },
  { value: 'urgent',   label: 'Urgent' },
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'neutral',  label: 'Neutral' }
];

const GoogleMaps = () => {
  const { showError } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  // Core data
  const [reviews, setReviews] = useState([]);
  const [analysisMap, setAnalysisMap] = useState({}); // review_id -> analysis
  const [branchSentiment, setBranchSentiment] = useState(0);
  const [branchUrgentCount, setBranchUrgentCount] = useState(0);

  // Branches
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(''); // '' = all

  // Filters (backend)
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [language, setLanguage] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  // Client-side filters
  const [includeNonAnalyzed, setIncludeNonAnalyzed] = useState(true);
  const [quickFilter, setQuickFilter] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsPerPage, setReviewsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // UI state
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calStart, setCalStart] = useState('');
  const [calEnd, setCalEnd] = useState('');

  // Search debounce
  const [searchTimeout, setSearchTimeout] = useState(null);

  // ===== Export state (carried from Reviews.js) =====
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDateType, setExportDateType] = useState('all');
  const [exportQuickDate, setExportQuickDate] = useState('today');
  const [exportCustomDate, setExportCustomDate] = useState('');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportSelectedBranches, setExportSelectedBranches] = useState([]);
  const [exportAllBranches, setExportAllBranches] = useState(true);
  const [exportFormat, setExportFormat] = useState('excel');
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [exportColumns, setExportColumns] = useState({
    reviewer_name: true,
    stars: true,
    review_text: true,
    review_text_translated: true,
    published_at_date: true,
    place_title: true,
    city: true,
    country_code: true,
    original_language: true,
    response_from_owner_text: true,
    branch_name: true
  });

  const availableColumns = {
    reviewer_name: 'Reviewer Name',
    reviewer_photo_url: 'Reviewer Photo URL',
    reviewer_number_of_reviews: 'Reviewer Reviews Count',
    is_local_guide: 'Is Local Guide',
    review_text: 'Review Text',
    review_text_translated: 'Translated Review Text',
    review_title: 'Review Title',
    stars: 'Rating (Stars)',
    rating: 'Rating (Number)',
    likes_count: 'Likes Count',
    published_at_date: 'Published Date',
    response_from_owner_text: 'Owner Response',
    response_from_owner_date: 'Owner Response Date',
    review_image_urls: 'Review Images',
    place_title: 'Place Name',
    category_name: 'Category',
    address: 'Address',
    city: 'City',
    state: 'State',
    country_code: 'Country',
    original_language: 'Language',
    branch_name: 'Branch Name',
    review_id: 'Review ID',
    place_id: 'Place ID',
    total_score: 'Total Score',
    reviews_count: 'Place Reviews Count',
    price: 'Price Level'
  };

  const quickDateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'quarter', label: 'Last 3 Months' },
    { value: 'year', label: 'Last Year' }
  ];

  const exportFormats = [
    { value: 'excel', label: 'Excel (.xlsx)', icon: FileSpreadsheet },
    { value: 'csv', label: 'CSV (.csv)', icon: FileText },
    { value: 'pdf', label: 'PDF (.pdf)', icon: FileDown }
  ];

  // ===== Data fetching =====
  const fetchBranches = async () => {
    try {
      const res = await api.get('/api/branches');
      if (res.data.success) {
        setBranches(res.data.branches);
      }
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: reviewsPerPage,
        sortBy,
        ...(searchTerm && { search: searchTerm }),
        ...(language !== 'all' && { language }),
        ...(ratingFilter !== 'all' && { rating: ratingFilter }),
        ...(appliedStartDate && { startDate: appliedStartDate }),
        ...(appliedEndDate && { endDate: appliedEndDate }),
        ...(selectedBranchId && { branchIds: selectedBranchId })
      });

      const res = await api.get(`/api/reviews?${params}`);
      if (res.data.success) {
        setReviews(res.data.reviews);
        setTotalCount(res.data.total);
        setTotalPages(res.data.pagination.totalPages);
      } else {
        setReviews([]);
      }
    } catch (e) {
      console.error('Failed to fetch reviews', e);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, reviewsPerPage, sortBy, searchTerm, language, ratingFilter, appliedStartDate, appliedEndDate, selectedBranchId]);

  // Fetch AI analyses for the branch (or all) and build map
  const fetchAnalyses = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: 10000 });
      if (selectedBranchId) params.append('branch_id', selectedBranchId);
      const res = await api.get(`/api/ai/review-analysis?${params}`);
      if (res.data.success) {
        const analyses = res.data.analyses || [];
        const map = {};
        let urgent = 0;
        const scores = [];
        for (const a of analyses) {
          if (a.review_id) map[a.review_id] = a;
          if (a.urgency_flag) urgent += 1;
          if (typeof a.sentiment_score === 'number') scores.push(a.sentiment_score);
        }
        setAnalysisMap(map);
        setBranchUrgentCount(urgent);
        setBranchSentiment(scores.length ? scores.reduce((s, v) => s + v, 0) / scores.length : 0);
      }
    } catch (e) {
      console.error('Failed to fetch analyses', e);
      setAnalysisMap({});
      setBranchUrgentCount(0);
      setBranchSentiment(0);
    }
  }, [selectedBranchId]);

  // Initial load
  useEffect(() => {
    fetchBranches();
  }, []);

  // Deep link from dashboard: open a specific review's modal on arrival
  useEffect(() => {
    const incoming = location.state?.openReview;
    if (!incoming) return;
    // If the incoming object carried an attached analysis (__analysis), fold it
    // into the analysisMap so the modal's AI section renders immediately.
    if (incoming.__analysis && incoming.review_id) {
      setAnalysisMap(prev => ({ ...prev, [incoming.review_id]: incoming.__analysis }));
    }
    setSelectedReview(incoming);
    // Clear the nav state so back/forward doesn't re-trigger the modal.
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    if (branches.length > 0 && exportSelectedBranches.length === 0) {
      setExportSelectedBranches(branches.map(b => b.id));
    }
  }, [branches, exportSelectedBranches.length]);

  // Refetch reviews when filters change
  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Refetch analyses when branch changes
  useEffect(() => {
    fetchAnalyses();
  }, [fetchAnalyses]);

  // ===== Derived: visible reviews (after client filters) =====
  const visibleReviews = useMemo(() => {
    return reviews.filter(r => {
      const analysis = analysisMap[r.review_id];
      if (!includeNonAnalyzed && !analysis) return false;

      if (quickFilter === 'all') return true;
      if (!analysis) return false;
      if (quickFilter === 'urgent')   return !!analysis.urgency_flag;
      if (quickFilter === 'positive') return analysis.sentiment === 'positive';
      if (quickFilter === 'negative') return analysis.sentiment === 'negative';
      if (quickFilter === 'neutral')  return analysis.sentiment === 'neutral';
      return true;
    });
  }, [reviews, analysisMap, includeNonAnalyzed, quickFilter]);

  // KPIs
  const avgRating = useMemo(() => {
    if (reviews.length === 0) return 0;
    const total = reviews.reduce((s, r) => s + (r.stars || 0), 0);
    return total / reviews.length;
  }, [reviews]);

  // ===== Handlers =====
  const handleSearchInput = (val) => {
    setSearchInput(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    const t = setTimeout(() => {
      setSearchTerm(val);
      setCurrentPage(1);
    }, 400);
    setSearchTimeout(t);
  };

  useEffect(() => () => { if (searchTimeout) clearTimeout(searchTimeout); }, [searchTimeout]);

  const applyDateFilter = () => {
    setAppliedStartDate(calStart);
    setAppliedEndDate(calEnd || calStart);
    setCurrentPage(1);
    setShowCalendar(false);
  };

  const clearDateFilter = () => {
    setCalStart(''); setCalEnd('');
    setAppliedStartDate(''); setAppliedEndDate('');
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearchInput(''); setSearchTerm('');
    setLanguage('all'); setRatingFilter('all'); setSortBy('newest');
    setQuickFilter('all'); setIncludeNonAnalyzed(true);
    clearDateFilter();
  };

  const changePage = (p) => {
    setCurrentPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ===== Formatting helpers =====
  const renderStars = (n) => {
    const count = Number(n) || 0;
    return '★'.repeat(count) + '☆'.repeat(Math.max(0, 5 - count));
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    try { return new Date(d).toLocaleDateString(); } catch { return 'Invalid'; }
  };

  const sentimentIcon = (sent) => {
    if (sent === 'positive') return <Smile size={14} />;
    if (sent === 'negative') return <Frown size={14} />;
    return <Meh size={14} />;
  };

  // ===== Export (carried from Reviews.js) =====
  const getCurrentDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };

  const getExportDateRange = () => {
    const today = getCurrentDate();
    switch (exportDateType) {
      case 'quick': {
        const day = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
        switch (exportQuickDate) {
          case 'today':     return { start: today, end: today };
          case 'yesterday': { const y = day(1); return { start: y, end: y }; }
          case 'week':      return { start: day(7), end: today };
          case 'month':     return { start: day(30), end: today };
          case 'quarter':   return { start: day(90), end: today };
          case 'year':      return { start: day(365), end: today };
          default:          return { start: null, end: null };
        }
      }
      case 'custom':
        if (exportCustomDate) {
          const d = new Date(exportCustomDate);
          return { start: d, end: d };
        }
        return { start: null, end: null };
      case 'range':
        if (exportStartDate && exportEndDate) {
          return { start: new Date(exportStartDate), end: new Date(exportEndDate) };
        }
        return { start: null, end: null };
      default:
        return { start: null, end: null };
    }
  };

  const fetchReviewsForExport = async () => {
    setExportLoading(true);
    try {
      const dateRange = getExportDateRange();
      const branchIds = exportAllBranches ? [] : exportSelectedBranches;

      let startParam = null, endParam = null;
      if (dateRange.start) {
        const s = new Date(dateRange.start); s.setHours(0,0,0,0);
        startParam = s.toISOString();
      }
      if (dateRange.end) {
        const e = new Date(dateRange.end); e.setHours(23,59,59,999);
        endParam = e.toISOString();
      }

      const params = new URLSearchParams({
        sortBy: 'newest',
        ...(startParam && { startDate: startParam }),
        ...(endParam && { endDate: endParam }),
        ...(branchIds.length > 0 && { branchIds: branchIds.join(',') })
      });

      const res = await api.get(`/api/export-reviews?${params}`);
      if (res.data.success) return res.data.reviews;
      throw new Error('Failed to fetch reviews for export');
    } finally {
      setExportLoading(false);
    }
  };

  const buildExportRow = (review) => {
    const row = {};
    Object.keys(exportColumns).forEach(key => {
      if (!exportColumns[key]) return;
      const colName = availableColumns[key];
      let value = review[key];
      if (key === 'is_local_guide') value = value ? 'Yes' : 'No';
      else if (key === 'review_image_urls' && Array.isArray(value)) value = value.join(', ');
      else if (key === 'published_at_date' && value) value = new Date(value).toLocaleDateString();
      else if (key === 'response_from_owner_date' && value) value = new Date(value).toLocaleDateString();
      else if (key === 'branch_name' && review.branches) value = review.branches.name || 'No Branch';
      row[colName] = value || 'N/A';
    });
    return row;
  };

  const exportDateSuffix = () => {
    const r = getExportDateRange();
    return r.start ? `-${r.start.toISOString().split('T')[0]}` : '';
  };

  const exportToExcel = (data) => {
    const rows = data.map(buildExportRow);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Google Reviews');
    ws['!cols'] = Object.keys(exportColumns).filter(k => exportColumns[k]).map(() => ({ wch: 20 }));
    XLSX.writeFile(wb, `google-reviews${exportDateSuffix()}.xlsx`);
  };

  const exportToCSV = (data) => {
    const rows = data.map(buildExportRow);
    const headers = Object.keys(rows[0] || {});
    const content = [
      headers.join(','),
      ...rows.map(r => headers.map(h => `"${String(r[h] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `google-reviews${exportDateSuffix()}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const exportToPDF = (data) => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(20); doc.text('Google Reviews Export', 20, 20);
      doc.setFontSize(12);
      const r = getExportDateRange();
      const dateInfo = r.start
        ? `Date Range: ${r.start.toLocaleDateString()} - ${r.end ? r.end.toLocaleDateString() : 'Present'}`
        : 'All Time';
      doc.text(dateInfo, 20, 35);
      doc.text(`Total Reviews: ${data.length}`, 20, 45);
      doc.text(`Export Date: ${new Date().toLocaleDateString()}`, 20, 55);

      const selectedCols = Object.keys(exportColumns).filter(k => exportColumns[k]);
      const headers = selectedCols.map(k => availableColumns[k]);
      const tableData = data.map(rev => selectedCols.map(k => {
        let v = rev[k];
        if (k === 'is_local_guide') v = v ? 'Yes' : 'No';
        else if (k === 'review_image_urls' && Array.isArray(v)) v = v.length ? `${v.length} images` : 'No images';
        else if (k === 'published_at_date' && v) v = new Date(v).toLocaleDateString();
        else if (k === 'response_from_owner_date' && v) v = new Date(v).toLocaleDateString();
        else if (k === 'branch_name' && rev.branches) v = rev.branches.name || 'No Branch';
        return v || 'N/A';
      }));

      doc.autoTable({
        head: [headers], body: tableData, startY: 70,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [16, 185, 129], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] }
      });
      doc.save(`google-reviews${exportDateSuffix()}.pdf`);
    } catch (e) {
      console.error('PDF export failed', e);
      alert('Error generating PDF. Please try Excel/CSV.');
    }
  };

  const handleExport = async () => {
    try {
      if (Object.values(exportColumns).filter(Boolean).length === 0) {
        alert('Please select at least one column to export.');
        return;
      }
      const data = await fetchReviewsForExport();
      if (!data || data.length === 0) {
        alert('No reviews found matching your criteria.');
        return;
      }
      switch (exportFormat) {
        case 'excel': exportToExcel(data); break;
        case 'csv':   exportToCSV(data); break;
        case 'pdf':   exportToPDF(data); break;
        default:      exportToExcel(data);
      }
      setShowExportPopup(false);
    } catch (e) {
      console.error('Export failed', e);
      showError('Export failed. Please try again.');
    }
  };

  const toggleColumn = (key) => setExportColumns(p => ({ ...p, [key]: !p[key] }));
  const selectAllColumns = () => {
    const all = {}; Object.keys(availableColumns).forEach(k => { all[k] = true; }); setExportColumns(all);
  };
  const deselectAllColumns = () => {
    const none = {}; Object.keys(availableColumns).forEach(k => { none[k] = false; }); setExportColumns(none);
  };
  const toggleExportBranch = (id) => {
    setExportSelectedBranches(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };
  const toggleAllExportBranches = () => {
    if (exportAllBranches) { setExportAllBranches(false); setExportSelectedBranches([]); }
    else { setExportAllBranches(true); setExportSelectedBranches(branches.map(b => b.id)); }
  };

  // ===== Render =====
  if (loading && reviews.length === 0) {
    return (
      <div className="gmaps-loading">
        <div className="gmaps-loading-spinner" />
        <p>Loading Google Maps reviews...</p>
      </div>
    );
  }

  const activeDateLabel = appliedStartDate || appliedEndDate
    ? `${appliedStartDate || '…'} → ${appliedEndDate || '…'}`
    : 'Date range';

  return (
    <div className="gmaps">
      {/* Header */}
      <div className="gmaps-header">
        <div className="gmaps-header-content">
          <h1 className="gmaps-title">Google Maps</h1>
          <p className="gmaps-subtitle">
            {totalCount.toLocaleString()} reviews total · {Object.keys(analysisMap).length.toLocaleString()} AI analyzed
          </p>
        </div>
        <div className="gmaps-header-actions">
          <select
            className="gmaps-branch-select"
            value={selectedBranchId}
            onChange={(e) => { setSelectedBranchId(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All branches</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>
                {b.name}{b.city ? ` (${b.city})` : ''}
              </option>
            ))}
          </select>
          <button
            className="gmaps-export-btn"
            onClick={() => setShowExportPopup(true)}
            disabled={reviews.length === 0}
          >
            <Download size={16} /> Export
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="gmaps-stats">
        <motion.div className="gmaps-stat-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <div className="gmaps-stat-label"><MessageSquare size={14} /> Total reviews</div>
          <div className="gmaps-stat-value">{totalCount.toLocaleString()}</div>
          <div className="gmaps-stat-sub">Matching current filters</div>
        </motion.div>
        <motion.div className="gmaps-stat-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="gmaps-stat-label"><Star size={14} /> Avg rating (page)</div>
          <div className={`gmaps-stat-value ${avgRating >= 4 ? 'positive' : avgRating >= 3 ? 'neutral' : 'negative'}`}>
            {avgRating.toFixed(2)}
          </div>
          <div className="gmaps-stat-sub">Out of 5</div>
        </motion.div>
        <motion.div className="gmaps-stat-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <div className="gmaps-stat-label"><TrendingUp size={14} /> Avg sentiment</div>
          <div className={`gmaps-stat-value ${branchSentiment >= 0.5 ? 'positive' : branchSentiment >= 0 ? 'neutral' : 'negative'}`}>
            {branchSentiment.toFixed(2)}
          </div>
          <div className="gmaps-stat-sub">AI sentiment · -1 to 1</div>
        </motion.div>
        <motion.div className="gmaps-stat-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="gmaps-stat-label"><AlertTriangle size={14} /> Urgent issues</div>
          <div className="gmaps-stat-value negative">{branchUrgentCount.toLocaleString()}</div>
          <div className="gmaps-stat-sub">Flagged by AI</div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="gmaps-filters">
        <div className="gmaps-search">
          <Search size={16} className="gmaps-search-icon" />
          <input
            type="text"
            className="gmaps-search-input"
            placeholder="Search reviews, reviewers, or places..."
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
          />
        </div>

        <div className="gmaps-filter-row">
          <select className="gmaps-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <select className="gmaps-select" value={ratingFilter} onChange={(e) => { setRatingFilter(e.target.value); setCurrentPage(1); }}>
            <option value="all">All ratings</option>
            <option value="5">5 stars</option>
            <option value="4">4 stars</option>
            <option value="3">3 stars</option>
            <option value="2">2 stars</option>
            <option value="1">1 star</option>
          </select>

          <select className="gmaps-select" value={language} onChange={(e) => { setLanguage(e.target.value); setCurrentPage(1); }}>
            <option value="all">All languages</option>
            <option value="en">English</option>
            <option value="ar">Arabic</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
          </select>

          <select className="gmaps-select" value={quickFilter} onChange={(e) => setQuickFilter(e.target.value)}>
            {QUICK_FILTERS.map(o => <option key={o.value} value={o.value}>AI: {o.label}</option>)}
          </select>

          <button
            className={`gmaps-date-btn ${(appliedStartDate || appliedEndDate) ? 'active' : ''}`}
            onClick={() => setShowCalendar(true)}
            title="Filter by date"
          >
            <Calendar size={14} /> {activeDateLabel}
          </button>

          <label className="gmaps-toggle">
            <input
              type="checkbox"
              checked={includeNonAnalyzed}
              onChange={(e) => setIncludeNonAnalyzed(e.target.checked)}
            />
            Include non-analyzed
          </label>

          <button className="gmaps-clear-btn" onClick={clearAllFilters}>
            <X size={14} /> Clear
          </button>
        </div>
      </div>

      {/* Cards */}
      {visibleReviews.length === 0 ? (
        <div className="gmaps-empty">
          <MessageSquare size={48} />
          <p>No reviews match your filters.</p>
        </div>
      ) : (
        <div className="gmaps-cards">
          {visibleReviews.map((r, idx) => {
            const a = analysisMap[r.review_id];
            return (
              <motion.div
                key={`${r.review_id}-${idx}`}
                className={`gmaps-card ${a?.urgency_flag ? 'urgent' : ''}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.03, 0.3) }}
                onClick={() => setSelectedReview(r)}
              >
                <div className="gmaps-card-top">
                  <div className="gmaps-reviewer">
                    <div className="gmaps-avatar">
                      {r.reviewer_photo_url
                        ? <img src={r.reviewer_photo_url} alt="" />
                        : <User size={18} />}
                    </div>
                    <div className="gmaps-reviewer-meta">
                      <span className="gmaps-reviewer-name">
                        {r.reviewer_name || 'Anonymous'}
                      </span>
                      <span className="gmaps-reviewer-sub">
                        <span className="gmaps-stars">{renderStars(r.stars)}</span>
                        <span>· {formatDate(r.published_at_date)}</span>
                      </span>
                    </div>
                  </div>

                  <div className="gmaps-badges">
                    {a ? (
                      <>
                        <span className={`gmaps-pill ${a.sentiment || 'neutral'}`}>
                          {sentimentIcon(a.sentiment)} {a.sentiment || 'neutral'}
                        </span>
                        {a.detected_emotion && (
                          <span className="gmaps-pill emotion">{a.detected_emotion}</span>
                        )}
                        {a.urgency_flag && (
                          <span className="gmaps-pill urgent">
                            <AlertTriangle size={12} /> Urgent
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="gmaps-pill ghost">Not analyzed</span>
                    )}
                  </div>
                </div>

                <div className="gmaps-card-body">
                  {(() => {
                    const text = r.review_text || r.review_text_translated || '';
                    if (!text) return <em style={{ color: 'rgba(255,255,255,0.4)' }}>No review text</em>;
                    return text.length > 320 ? `${text.substring(0, 320)}…` : text;
                  })()}
                </div>

                <div className="gmaps-card-foot">
                  {r.branches?.name && <span>🏢 {r.branches.name}</span>}
                  {r.review_image_urls?.length > 0 && (
                    <span><ImageIcon size={12} /> {r.review_image_urls.length} images</span>
                  )}
                  {r.response_from_owner_text && <span>💬 Owner replied</span>}
                  {r.original_language && <span>🌐 {r.original_language}</span>}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {reviews.length > 0 && (
        <div className="gmaps-pagination">
          <div>
            Page {currentPage} of {totalPages} · {reviews.length} shown ({visibleReviews.length} after AI filter) · {totalCount.toLocaleString()} total
          </div>
          <div className="gmaps-page-btns">
            <select
              className="gmaps-select"
              value={reviewsPerPage}
              onChange={(e) => { setReviewsPerPage(parseInt(e.target.value, 10)); setCurrentPage(1); }}
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
            <button className="gmaps-page-btn" onClick={() => changePage(1)} disabled={loading || currentPage === 1}>First</button>
            <button className="gmaps-page-btn" onClick={() => changePage(Math.max(1, currentPage - 1))} disabled={loading || currentPage === 1}>Prev</button>
            <button className="gmaps-page-btn" onClick={() => changePage(Math.min(totalPages, currentPage + 1))} disabled={loading || currentPage >= totalPages}>Next</button>
            <button className="gmaps-page-btn" onClick={() => changePage(totalPages)} disabled={loading || currentPage === totalPages}>Last</button>
            {loading && <RefreshCw size={14} className="spinning" />}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedReview && (
          <div className="gmaps-modal-overlay" onClick={() => setSelectedReview(null)}>
            <motion.div
              className="gmaps-modal"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="gmaps-modal-head">
                <h2>Review details</h2>
                <button className="gmaps-close" onClick={() => setSelectedReview(null)}>×</button>
              </div>
              <div className="gmaps-modal-body">
                {/* Reviewer */}
                <div className="gmaps-modal-section" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div className="gmaps-avatar" style={{ width: 56, height: 56 }}>
                    {selectedReview.reviewer_photo_url
                      ? <img src={selectedReview.reviewer_photo_url} alt="" />
                      : <User size={24} />}
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 600 }}>
                      {selectedReview.reviewer_name || 'Anonymous'}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginTop: 4 }}>
                      {[1,2,3,4,5].map(s => (
                        <Star
                          key={s}
                          size={14}
                          style={{
                            color: s <= (selectedReview.stars || 0) ? '#f59e0b' : 'rgba(255,255,255,0.2)',
                            fill: s <= (selectedReview.stars || 0) ? '#f59e0b' : 'transparent'
                          }}
                        />
                      ))}
                      <span style={{ marginLeft: 8 }}>{selectedReview.stars}/5</span>
                      {selectedReview.is_local_guide && (
                        <span style={{ marginLeft: 10, color: '#10b981' }}>🏆 Local Guide</span>
                      )}
                      {selectedReview.reviewer_number_of_reviews > 0 && (
                        <span style={{ marginLeft: 10 }}>
                          · {selectedReview.reviewer_number_of_reviews} reviews
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* AI Analysis (if any) */}
                {(() => {
                  const a = analysisMap[selectedReview.review_id];
                  if (!a) return null;
                  return (
                    <div className="gmaps-modal-section gmaps-ai-box">
                      <h3><Brain size={16} /> AI analysis</h3>
                      <div className="gmaps-kv">
                        <dt>Sentiment</dt>
                        <dd>
                          <span className={`gmaps-pill ${a.sentiment || 'neutral'}`}>
                            {sentimentIcon(a.sentiment)} {a.sentiment || 'neutral'}
                          </span>
                          {typeof a.sentiment_score === 'number' && (
                            <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.6)' }}>
                              ({a.sentiment_score.toFixed(2)})
                            </span>
                          )}
                        </dd>
                        {a.detected_emotion && (<><dt>Emotion</dt><dd>{a.detected_emotion}</dd></>)}
                        {a.topics?.length > 0 && (
                          <>
                            <dt>Topics</dt>
                            <dd>
                              <div className="gmaps-tag-row">
                                {a.topics.map((t, i) => <span key={i} className="gmaps-pill emotion">{t}</span>)}
                              </div>
                            </dd>
                          </>
                        )}
                        {a.pros?.length > 0 && (
                          <>
                            <dt>Positives</dt>
                            <dd><ul style={{ marginLeft: '1rem' }}>{a.pros.map((p,i) => <li key={i}>{p}</li>)}</ul></dd>
                          </>
                        )}
                        {a.cons?.length > 0 && (
                          <>
                            <dt>Issues</dt>
                            <dd><ul style={{ marginLeft: '1rem' }}>{a.cons.map((p,i) => <li key={i}>{p}</li>)}</ul></dd>
                          </>
                        )}
                        {a.urgency_flag && (
                          <>
                            <dt>Urgent</dt>
                            <dd style={{ color: '#fecaca' }}>
                              <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                              {a.urgency_reason || 'Flagged as urgent'}
                            </dd>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Review text */}
                <div className="gmaps-modal-section">
                  <h3>{selectedReview.review_title || 'Review text'}</h3>
                  <p>{selectedReview.review_text || selectedReview.review_text_translated || 'No review text.'}</p>
                </div>

                {/* Owner response */}
                {selectedReview.response_from_owner_text && (
                  <div className="gmaps-modal-section gmaps-owner-response">
                    <h3>💬 Owner response</h3>
                    <p>{selectedReview.response_from_owner_text}</p>
                    {selectedReview.response_from_owner_date && (
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginTop: 4 }}>
                        {formatDate(selectedReview.response_from_owner_date)}
                      </p>
                    )}
                  </div>
                )}

                {/* Images */}
                {selectedReview.review_image_urls?.length > 0 && (
                  <div className="gmaps-modal-section">
                    <h3><ImageIcon size={16} /> Images ({selectedReview.review_image_urls.length})</h3>
                    <div className="gmaps-gallery">
                      {selectedReview.review_image_urls.map((url, i) => (
                        <img key={i} src={url} alt={`review ${i + 1}`} onClick={() => window.open(url, '_blank')} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Meta */}
                <div className="gmaps-modal-section">
                  <h3>Details</h3>
                  <dl className="gmaps-kv">
                    <dt>Published</dt><dd>{formatDate(selectedReview.published_at_date)}</dd>
                    <dt>Language</dt><dd>{selectedReview.original_language || 'Unknown'}</dd>
                    <dt>Place</dt><dd>{selectedReview.place_title || 'N/A'}</dd>
                    <dt>Location</dt><dd>{[selectedReview.city, selectedReview.country_code].filter(Boolean).join(', ') || 'N/A'}</dd>
                    {selectedReview.branches?.name && (<><dt>Branch</dt><dd>{selectedReview.branches.name}</dd></>)}
                    {selectedReview.likes_count > 0 && (<><dt>Likes</dt><dd>{selectedReview.likes_count}</dd></>)}
                    {selectedReview.review_id && (<><dt>Review ID</dt><dd style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{selectedReview.review_id}</dd></>)}
                  </dl>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Calendar modal */}
      <AnimatePresence>
        {showCalendar && (
          <div className="gmaps-modal-overlay" onClick={() => setShowCalendar(false)}>
            <motion.div
              className="gmaps-modal"
              style={{ maxWidth: 440 }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="gmaps-modal-head">
                <h2>Filter by date</h2>
                <button className="gmaps-close" onClick={() => setShowCalendar(false)}>×</button>
              </div>
              <div className="gmaps-modal-body">
                <div className="gmaps-modal-section">
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>From</label>
                  <input
                    type="date"
                    className="gmaps-search-input"
                    style={{ paddingLeft: '1rem', marginTop: 4 }}
                    value={calStart}
                    onChange={(e) => setCalStart(e.target.value)}
                  />
                </div>
                <div className="gmaps-modal-section">
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>To (optional)</label>
                  <input
                    type="date"
                    className="gmaps-search-input"
                    style={{ paddingLeft: '1rem', marginTop: 4 }}
                    value={calEnd}
                    onChange={(e) => setCalEnd(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  {(appliedStartDate || appliedEndDate) && (
                    <button className="gmaps-clear-btn" onClick={clearDateFilter}>Clear</button>
                  )}
                  <button className="gmaps-export-btn" onClick={applyDateFilter} disabled={!calStart}>
                    Apply
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Export popup */}
      <AnimatePresence>
        {showExportPopup && (
          <div className="gmaps-modal-overlay" onClick={() => setShowExportPopup(false)}>
            <motion.div
              className="gmaps-modal"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="gmaps-modal-head">
                <h2>Export reviews</h2>
                <button className="gmaps-close" onClick={() => setShowExportPopup(false)}>×</button>
              </div>
              <div className="gmaps-modal-body">
                {/* Date */}
                <div className="gmaps-modal-section">
                  <h3>📅 Date range</h3>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    {['all', 'quick', 'custom', 'range'].map(t => (
                      <label key={t} style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                        <input
                          type="radio"
                          name="gmapsExportDateType"
                          value={t}
                          checked={exportDateType === t}
                          onChange={(e) => setExportDateType(e.target.value)}
                          style={{ marginRight: 4 }}
                        />
                        {t === 'all' ? 'All time' : t === 'quick' ? 'Quick' : t === 'custom' ? 'Single date' : 'Range'}
                      </label>
                    ))}
                  </div>

                  {exportDateType === 'quick' && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {quickDateOptions.map(o => (
                        <button
                          key={o.value}
                          className={`gmaps-date-btn ${exportQuickDate === o.value ? 'active' : ''}`}
                          onClick={() => setExportQuickDate(o.value)}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {exportDateType === 'custom' && (
                    <input
                      type="date"
                      className="gmaps-search-input"
                      style={{ paddingLeft: '1rem' }}
                      value={exportCustomDate}
                      onChange={(e) => setExportCustomDate(e.target.value)}
                    />
                  )}
                  {exportDateType === 'range' && (
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <input
                        type="date"
                        className="gmaps-search-input"
                        style={{ paddingLeft: '1rem' }}
                        value={exportStartDate}
                        onChange={(e) => setExportStartDate(e.target.value)}
                      />
                      <input
                        type="date"
                        className="gmaps-search-input"
                        style={{ paddingLeft: '1rem' }}
                        value={exportEndDate}
                        onChange={(e) => setExportEndDate(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                {/* Branches */}
                <div className="gmaps-modal-section">
                  <h3>🏢 Branches</h3>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#fff' }}>
                    <input type="checkbox" checked={exportAllBranches} onChange={toggleAllExportBranches} />
                    Export all branches
                  </label>
                  {!exportAllBranches && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.4rem', marginTop: '0.5rem' }}>
                      {branches.map(b => (
                        <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                          <input
                            type="checkbox"
                            checked={exportSelectedBranches.includes(b.id)}
                            onChange={() => toggleExportBranch(b.id)}
                          />
                          {b.name}{b.city ? ` (${b.city})` : ''}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Columns */}
                <div className="gmaps-modal-section">
                  <h3>📊 Columns</h3>
                  <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.5rem' }}>
                    <button className="gmaps-page-btn" onClick={selectAllColumns}>Select all</button>
                    <button className="gmaps-page-btn" onClick={deselectAllColumns}>Deselect all</button>
                    <button className="gmaps-page-btn" onClick={() => setShowColumnSelector(!showColumnSelector)}>
                      {showColumnSelector ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {showColumnSelector ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {showColumnSelector && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.35rem', maxHeight: 260, overflowY: 'auto', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
                      {Object.entries(availableColumns).map(([k, label]) => (
                        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                          <input
                            type="checkbox"
                            checked={!!exportColumns[k]}
                            onChange={() => toggleColumn(k)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  )}
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                    Selected: {Object.values(exportColumns).filter(Boolean).length} of {Object.keys(availableColumns).length}
                  </div>
                </div>

                {/* Format */}
                <div className="gmaps-modal-section">
                  <h3>💾 Format</h3>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {exportFormats.map(f => (
                      <label key={f.value} style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <input
                          type="radio"
                          name="gmapsExportFormat"
                          value={f.value}
                          checked={exportFormat === f.value}
                          onChange={(e) => setExportFormat(e.target.value)}
                        />
                        <f.icon size={14} /> {f.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', paddingTop: '0.5rem' }}>
                  <button className="gmaps-page-btn" onClick={() => setShowExportPopup(false)} disabled={exportLoading}>
                    Cancel
                  </button>
                  <button className="gmaps-export-btn" onClick={handleExport} disabled={exportLoading}>
                    {exportLoading ? <><RefreshCw size={14} className="spinning" /> Preparing…</> : <><Download size={14} /> Export</>}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GoogleMaps;
